import React, { useState } from 'react';

/**
 * Text input component with label.
 * Design: High-contrast borders, monospace font.
 *
 * When `type === "password"`, an inline eye toggle appears at the right
 * edge to show/hide the typed password. The toggle inherits the same hover
 * inversion as the rest of the design system and shifts the input's right
 * padding so long passwords don't run under it.
 */
export default function Input({
  label,
  id,
  type = 'text',
  placeholder,
  value,
  onChange,
  error,
  disabled = false,
  required = false,
  className = '',
  ...props
}) {
  const inputId = id || label?.toLowerCase().replace(/\s+/g, '-');
  const isPassword = type === 'password';
  const [showPassword, setShowPassword] = useState(false);
  const effectiveType = isPassword && showPassword ? 'text' : type;

  return (
    <div className={`w-full ${className}`}>
      {label && (
        <label
          htmlFor={inputId}
          className="block font-mono text-sm font-semibold uppercase tracking-wider mb-2 text-black"
        >
          {label}
          {required && <span className="text-red-600 ml-1">*</span>}
        </label>
      )}
      <div className="relative">
        <input
          id={inputId}
          type={effectiveType}
          placeholder={placeholder}
          value={value}
          onChange={onChange}
          disabled={disabled}
          required={required}
          className={`
            w-full px-4 py-3
            border-2 border-black
            bg-white text-black
            font-mono text-sm
            placeholder:text-gray-400
            focus:outline-none focus:ring-2 focus:ring-black focus:ring-offset-2
            disabled:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-50
            ${isPassword ? 'pr-14' : ''}
            ${error ? 'border-red-600 focus:ring-red-600' : ''}
          `}
          {...props}
        />
        {isPassword && (
          <button
            type="button"
            tabIndex={-1}
            onClick={() => setShowPassword((v) => !v)}
            aria-label={showPassword ? 'Hide characters' : 'Show characters'}
            disabled={disabled}
            className="absolute right-2 top-1/2 -translate-y-1/2 w-10 h-10 flex items-center justify-center text-black hover:bg-black hover:text-white border-2 border-transparent hover:border-black transition-colors disabled:opacity-40"
          >
            {showPassword ? <EyeOffIcon /> : <EyeIcon />}
          </button>
        )}
      </div>
      {error && (
        <p className="mt-2 font-mono text-xs text-red-600 uppercase tracking-wider">
          {error}
        </p>
      )}
    </div>
  );
}

function EyeIcon() {
  return (
    <svg width={20} height={20} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="square" strokeLinejoin="miter" aria-hidden>
      <path d="M2 12s4-7 10-7 10 7 10 7-4 7-10 7-10-7-10-7z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  );
}

function EyeOffIcon() {
  return (
    <svg width={20} height={20} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="square" strokeLinejoin="miter" aria-hidden>
      <path d="M2 2l20 20" />
      <path d="M6.71 6.71C3.92 8.45 2 12 2 12s4 7 10 7c2.05 0 3.84-.66 5.31-1.59" />
      <path d="M9.88 5.11A10.86 10.86 0 0 1 12 5c6 0 10 7 10 7-.65 1.13-1.51 2.27-2.59 3.27" />
      <path d="M14.12 14.12a3 3 0 1 1-4.24-4.24" />
    </svg>
  );
}
